/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import {
  FeaturedVideos as FeaturedVideosData,
  EmbeddedVideo as EmbeddedVideoBlock,
} from '@/payload-types'
import Image from 'next/image'
import Card, { CardTitle } from '../card/card'
import { EmbeddedVideo } from '../embedded-video/embeddedVideo'
import LightGalleryWrapper from '../galleries/light-gallery/lightGallery'
import { EmbeddedVideoItem } from '../galleries/light-gallery/helperFunctionsLightGallery'

export const FeaturedVideos = ({ videos }: { videos: FeaturedVideosData }) => {
  const renderMedia = (media: FeaturedVideosData | string | null) => {
    if (!media || typeof media === 'string') return null

    return (
      <div>
        <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
          <EmbeddedVideo embeddedVideoData={media as unknown as EmbeddedVideoBlock} />
        </div>
      </div>
    )
  }

  const renderAdditionalItem = (
    item: FeaturedVideosData,
    index: number,
    onOpen: (i: number) => void,
  ) => {
    if (!item) return null

    return (
      <div
        key={index}
        className="flex gap-3 py-2 border-t border-gray-200 cursor-pointer"
        onClick={(e) => {
          e.preventDefault()
          onOpen(index)
        }}
      >
        <div className="xl:min-w-[200px] xl:w-[200px] md:min-w-[300px] md:w-[300px] w-[160px] min-w-[160px]">
          <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden pointer-events-none">
            <EmbeddedVideo embeddedVideoData={item as unknown as EmbeddedVideoBlock} />
          </div>
        </div>
        <div>
          {renderMediaTag()}
          <h3
            className="text-base font-medium line-clamp-2 break-words"
            style={{ overflowWrap: 'anywhere' }}
          >
            {item.title}
          </h3>
        </div>
      </div>
    )
  }

  const renderMediaTag = () => {
    return (
      <span className="flex text-sm text-cm-red font-semibold uppercase tracking-wide my-2 items-center">
        <Image
          src="/images/favicons/video-camera.svg"
          alt="Video"
          width={18}
          height={18}
          className="mr-1"
        />
        VIDEO
      </span>
    )
  }

  const featuredVideo = {
    videoId: videos.videoId,
    title: videos.title,
    description: videos.description,
    caption: videos.caption,
    source: videos.source,
  }

  return (
    <div className="PageInternalContainer w-full flex justify-center">
      <div className="lg:w-[70vw] w-[90vw] flex flex-col justify-center items-start py-12">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">See Our Featured Videos</h2>
          <p className="text-lg w-[90vw] lg:w-[60vw]">
            Find out more about the captivating manifestations of racism, anti-Zionism and
            antisemitism from our uniquely trained perspective.
          </p>
        </div>

        <div className="xl:grid grid-cols-1 xl:grid-cols-5 gap-8 flex flex-col w-full">
          {/* Left Column - Featured Content */}
          <div className="lg:col-span-3">
            <div className="rounded-lg overflow-hidden">
              {featuredVideo.videoId && renderMedia(featuredVideo)}

              <div className="pt-4">
                <div className="flex tracking-wide my-2 items-center">
                  <span className="text-sm font-semibold uppercase text-black bg-[#FFDF00] px-2 py-1 mr-2 rounded-md">
                    FEATURED
                  </span>
                  {renderMediaTag()}
                </div>

                <h3 className="text-xl font-bold mb-3">{featuredVideo.title || 'Untitled'}</h3>
                {featuredVideo.description && (
                  <p className="leading-relaxed text-base max-h-[200px] overflow-y-auto">
                    {featuredVideo.description}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - More Content */}
          <div className="lg:col-span-2">
            <LightGalleryWrapper externalVideos={videos.additionalVideos as EmbeddedVideoItem[]}>
              {({ onOpen }: { onOpen: (index: number) => void }) => (
                <Card className="max-h-[500px] overflow-y-auto shadow-md">
                  <CardTitle className="flex justify-between border-none mb-0">
                    <div className="flex gap-3 items-end">
                      <div className="leading-none">More</div>
                    </div>
                  </CardTitle>

                  <div className="relative group my-2 border-b border-gray-200 last:border-b-0">
                    {videos.additionalVideos?.map((item, index) =>
                      renderAdditionalItem(item, index, onOpen),
                    )}
                  </div>
                </Card>
              )}
            </LightGalleryWrapper>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FeaturedVideos
